<template>
  <el-dialog
    :visible="visible"
    width="900px"
    @update:visible="onCancel"
    title="表示列のカスタマイズ"
    custom-class="with-title"
  >
    <div class="table-setting-dialog">
      <div class="list-container">
        <!-- 左ペイン: 全マスタ列（チェックで表示 / 外して非表示） -->
        <div class="selected-container">
          <div v-for="column in allColumns" :key="column.key" class="selected-item">
            <el-checkbox :value="isVisible(column.key)" @change="toggle(column.key)">
              {{ column.label }}
            </el-checkbox>
          </div>
        </div>

        <!-- 右ペイン: 表示中の列のみ D&D で並べ替え -->
        <div class="sort-container">
          <div class="sort-text">
            選択された列 {{ orderedVisible.length }} 件、ドラッグ＆ドロップで並び替えができます。
          </div>

          <draggable :list="orderedVisible" @change="onDragChange" :animation="200" easing="cubic-bezier(1, 0, 0, 1)">
            <div v-for="column in orderedVisible" :key="column.key" class="sort-item">
              <div class="sort-item-text-list">
                <i class="mdi mdi-drag" />
                <div class="sort-item-text-item">{{ column.label }}</div>
              </div>
            </div>
          </draggable>
        </div>
      </div>
    </div>
    <template slot="footer">
      <div class="table-setting-dialog__footer">
        <el-button size="small" @click="onResetToDefault">デフォルトに戻す</el-button>
        <div class="table-setting-dialog__footer-actions">
          <el-button size="small" @click="onCancel">キャンセル</el-button>
          <el-button size="small" type="primary" @click="onSave">保存</el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue'
import draggable from 'vuedraggable'

import { TrackingService } from '@/client/services'
import { type ColumnKey } from '@/typings/twitter_benchmark_campaign_posts'

import { useTableSettings } from '../../../hooks/useTableSettings'
import { useTwBenchmarkPostsStore } from '../../../store/useTwBenchmarkPostsStore'
import {
  DEFAULT_TABLE_COLUMNS,
  DEFAULT_TABLE_COLUMNS_KEY,
  type TwBenchmarkDefaultTableColums
} from '../../../utils/columns'

/**
 * 表示列カスタマイズダイアログ（draftOrder を編集し、保存時に SettingsStore へ全置換保存する）。
 */
export default defineComponent({
  name: 'TwBenchmarkTableSettingDialog',

  components: {
    draggable
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],

  setup(props, { emit }) {
    const { draftOrder, open, toggle, reorder, save, cancel, resetToDefault, isVisibleInDraft } = useTableSettings()
    const postsStore = useTwBenchmarkPostsStore()

    watch(
      () => props.visible,
      isOpen => {
        if (isOpen) open()
      },
      { immediate: true }
    )

    const orderedVisible = computed<TwBenchmarkDefaultTableColums[]>(() =>
      draftOrder.value
        .map(key => DEFAULT_TABLE_COLUMNS_KEY.get(key))
        .filter((col): col is TwBenchmarkDefaultTableColums => col !== undefined)
    )

    const onDragChange = (event: { moved?: { oldIndex: number; newIndex: number } }): void => {
      const moved = event.moved
      if (!moved) return
      TrackingService.sendEvent('click:競合キャンペーン分析|表示列のカスタマイズ|並び替え')
      const next = [...draftOrder.value]
      const [target] = next.splice(moved.oldIndex, 1)
      next.splice(moved.newIndex, 0, target)
      reorder(next)
    }

    const allColumns = computed<TwBenchmarkDefaultTableColums[]>(() => [...DEFAULT_TABLE_COLUMNS])

    const onSave = async (): Promise<void> => {
      TrackingService.sendEvent('click:競合キャンペーン分析|表示列のカスタマイズ|保存')
      const isSuccess = await save()
      if (isSuccess) await postsStore.refresh()
      emit('close')
    }

    const onCancel = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|表示列のカスタマイズ|キャンセル')
      cancel()
      emit('close')
    }

    const onResetToDefault = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|表示列のカスタマイズ|デフォルトに戻す')
      resetToDefault()
    }

    const onToggleColumn = (key: ColumnKey): void => {
      TrackingService.sendEvent('check:競合キャンペーン分析|表示列のカスタマイズ|列の表示切替')
      toggle(key)
    }

    return {
      orderedVisible,
      onDragChange,
      allColumns,
      isVisible: (key: ColumnKey) => isVisibleInDraft(key),
      toggle: onToggleColumn,
      onSave,
      onCancel,
      onResetToDefault
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

:deep(.el-dialog__header) {
  border-bottom: 1px solid $border-color;
}

:deep(.el-dialog__body) {
  padding: 0;
}

:deep(.el-dialog__footer) {
  border-top: 1px solid $border-color;
}

.table-setting-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-setting-dialog__footer-actions {
  display: flex;
  gap: $spacing-unit;
}

.tw-benchmark-setting__dialog-title {
  font-size: $font-large-size;
  font-weight: $font-weight-bold;
  color: $text-color;
}

.tw-benchmark-setting__help {
  margin-left: $spacing-unit;
  font-size: 18px;
  color: $sub-text-color;
  cursor: pointer;
}

.table-setting-dialog {
  .list-container {
    display: flex;
    align-items: stretch;
  }

  .selected-container {
    box-sizing: border-box;
    width: 50%;
    max-height: 75vh;
    padding: $spacing-unit * 2;
    overflow-y: scroll;
  }

  .selected-item {
    display: flex;
    align-items: center;
    margin: 10px 0 0;

    &:first-child {
      margin: 0;
    }

    :deep(.el-checkbox) {
      box-shadow: none;
    }
  }

  .sort-container {
    box-sizing: border-box;
    width: 50%;
    max-height: 75vh;
    padding: $spacing-unit * 2;
    overflow-y: scroll;
    background-color: $background-color;
    border-left: 1px solid $border-color;
  }

  .sort-text {
    margin-bottom: $spacing-unit;
    color: $sub-text-color;
  }

  .sort-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 0 $spacing-unit;
    margin: $spacing-unit 0 0;
    cursor: move;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 1px rgb(0 0 0 / 10%);

    &:first-child {
      margin: 0;
    }
  }

  .sort-item-text-list {
    display: flex;
    align-items: center;

    .mdi-drag {
      font-size: 18px;
      color: $sub-text-color;
      cursor: grab;
    }

    .sort-item-text-item {
      margin: 0;
      font-size: 14px;
      line-height: 30px;
      color: $text-color;
    }
  }

  .sort-item-button {
    appearance: none;
    cursor: pointer;
    outline: none;
    background: transparent;
    border: none;
    border-radius: 0;
  }
}
</style>
